//import axios from "axios";
import axios from "../../interceptors/axiosInstance";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";
import { config } from "../../config";
import { MessageContext } from "../../context/MessageContext";
import {
  decimalNumberWithHigherLimitRegex,
  negativeNumberWithHigherLimitRegex,
  decimalNumberWith2DecimalRegex,
  whiteSpaceStringRegex,
} from "../../utils/ValidationRules";
import {
  getBufferErrorMessage,
  getErrorMessage,
} from "../../utils/ErrorMessage";
import { createExcelFile } from "../../utils/createFile";
import UserContext from "../../context/UserContext";

const PPAContext = createContext();

const tabs = [
  { id: "1", label: "Select Model" },
  { id: "2", label: "Enter PPA Parameters" },
  { id: "3", label: "Modify Benchmark Parameters" },
  { id: "4", label: "Result" },
];

export const PPAContextProvider = ({ children }) => {
  // DO NOT CHANGE
  // These are hidden sheetnames in excel to ensure the correct template is being uploaded
  const templateNames = {
    benchmarkFile: "Benchmark",
    volume1CurveFile: "Volume1Curve",
    volume2CurveFile: "Volume2Curve",
    price1CurveFile: "Price1Curve",
    powerPriceCurveFile: "PowerPriceCurve",
    gooPriceCurveFile: "GOOPriceCurve",
    carbonIntensityOfGridFile: "CarbonIntensityOfGrid",
    volumeCurveFile: "volumeCurve",
    blackMarketPriceCurve: "blackMarketPriceCurve",
    greenMarketPriceCurve: "greenMarketPriceCurve",
    mlfCurve: "mlfCurve",
    mlfCurveAtInception: "mlfCurveAtInception",
    dlfCurve: "dlfCurve",
    dlfCurveAtInception: "dlfCurveAtInception",
    inflationCurve: "inflationCurve",
    cpiCurve: "cpiCurve",
    riskFreeRateCurve: "riskFreeRateCurve",
    assetCreditRiskAdjustmentCurve: "assetCreditRiskAdjustmentCurve",
    liabilityCreditRiskAdjustmentCurve: "liabilityCreditRiskAdjustmentCurve",
    ppaEscalationCurve: "ppaEscalationCurve",
    ppaPriceCurve: "ppaPriceCurve",
    greenMarketPriceCurveAtInception: "greenMarketPriceCurveAtInception",
    blackMarketPriceCurveAtInception: "blackMarketPriceCurveAtInception",
    volumeCurveAtInception: "volumeCurveAtInception",
    curtailmentFactor: "curtailmentFactor",
    curtailmentFactorAtInception: "curtailmentFactorAtInception",
    unbundlingFactor: "unbundlingFactor",
    volumeCurve: "volumeCurve",
    inflationRateCurve: "inflationRateCurve",
    discountRateCurve: "discountRateCurve",
    priceForecastCurve: "priceForecastCurve",
    chargeForecastPriceCurve: "chargeForecastPriceCurve",
    chargeMLFCurve: "chargeMLFCurve",
    disChargeMLFCurve: "disChargeMLFCurve",
  };

  const initialState = {
    formData: {},
    resultData: {},
    scenarioResults: [],
    ppaErrors: [],
    isLoading: false,
    tabId: "1",
  };

  const [activeTabId, setActiveTabId] = useState(initialState.tabId);
  const [formData, setFormData] = useState(initialState.formData);
  const [errors, setErrors] = useState(initialState.ppaErrors);
  const [disableNext, setDisableNext] = useState(true);
  const [disableCalculate, setDisableCalculate] = useState(true);
  const [isLoading, setLoading] = useState(initialState.isLoading);
  const [resultData, setResultData] = useState(initialState.resultData);
  const [scenarioResults, setScenarioResults] = useState(
    initialState.scenarioResults,
  );
  const [forecastedDates, setForecastedDates] = useState([]);
  const { ErrorToast } = useContext(MessageContext);
  const { authUser } = useContext(UserContext);

  const {
    modelStartDate,
    contractEndDate,
    ppaLocation,
    useBenchmarkData,
    modelType,
    valuationAtInception,
    mlfIncludedInVolume,
    dlfIncludedInVolume,
    curtailmentIncludedInVolume,
  } = formData;

  const fetchForecastedDates = async (
    location,
    startDate,
    endDate,
    requiredBenchmarData,
  ) => {
    try {
      SetIsLoading(true);
      const result = await axios.get(
        `${config.BASE_URL}/api/ppa/forecasted-dates?startDate=${startDate}&endDate=${endDate}&location=${location}&requiredBenchmarData=${requiredBenchmarData.join(",")}`,
      );
      if (result.data.length >= 0) {
        setForecastedDates(
          result.data?.map((item) => ({ label: item, value: item })),
        );
      }
      SetIsLoading(false);
    } catch (error) {
      ErrorToast("Failed to get available benchmark data");
      setForecastedDates([]);
      SetIsLoading(false);
    }
  };

  useEffect(() => {
    // If a valid value has been provided for location, start and end date,
    // Then fetch the available forecasted dates for which benchmark data is available.
    if (
      modelStartDate &&
      contractEndDate &&
      ppaLocation &&
      useBenchmarkData === "yes"
    ) {
      const requiredBenchmarData = [];

      if (valuationAtInception === "yes") {
        requiredBenchmarData.push("greenMarketPriceCurve");
      }
      if (mlfIncludedInVolume === "no") {
        requiredBenchmarData.push("mlfCurve");
      }
      if (dlfIncludedInVolume === "no") {
        requiredBenchmarData.push("dlfCurve");
      }

      if (curtailmentIncludedInVolume === "no") {
        requiredBenchmarData.push("curtailmentFactor");
      }

      fetchForecastedDates(
        ppaLocation,
        modelStartDate,
        contractEndDate,
        requiredBenchmarData,
      );
    }
  }, [
    modelStartDate,
    contractEndDate,
    ppaLocation,
    useBenchmarkData,
    valuationAtInception,
    mlfIncludedInVolume,
    dlfIncludedInVolume,
    curtailmentIncludedInVolume,
  ]);

  useEffect(() => {
    if (
      modelType === "existing_PPA" &&
      authUser?.client?.useEYBenchmarkData === "no"
    ) {
      setFormData((prev) => ({
        ...prev,
        useBenchmarkData: "no",
        forecastedDate: "",
        iscustomised: "Yes",
        blackMarketPrice: "yes",
        greenMarketPrice: "yes",
        uploadMLFCurve: "yes",
        uploadDLFCurve: "yes",
        uploadCurtailmentFactor: "yes",
        inflation: "yes",
        riskFreeRate: "yes",
        assetCreditRiskAdjustment: "yes",
        liabilityCreditRiskAdjustment: "yes",
      }));
    }
  }, [authUser?.client?.useEYBenchmarkData, modelType]);

  const SetFormData = (newData) => {
    if (newData) {
      setFormData(newData);
      validateValues(newData);
    }
  };

  const SetErrors = (newErrors) => {
    if (newErrors) {
      setErrors(newErrors);
    }
  };

  const SetIsLoading = (isLoading) => {
    setLoading(isLoading);
  };

  const SetActiveTabId = (tabId) => {
    if (tabId) {
      setActiveTabId(tabId);
    }
  };

  const SetResultData = (newData) => {
    if (newData) {
      setResultData(newData);
    }
  };

  const DownloadBenchmarkTemplate = async (
    country,
    modelType = "new_PPA",
    benchMarkType = "",
    startDate = "",
    endDate,
  ) => {
    let params = {
      country,
    };

    const configs = {
      method: "post",
      url: `${config.BASE_URL}/api/ppa/downloadBenchmarkTemplate`,
      headers: {},
      data: params,
      responseType: "arraybuffer",
    };

    if (modelType === "existing_PPA") {
      configs.url = `${config.BASE_URL}/api/ppa/download-valuation-template`;
      configs.data = {
        fileName: benchMarkType,
        country,
        startDate,
        endDate,
        freequency: "monthly",
        type: "benchmark",
      };
    }
    try {
      SetIsLoading(true);
      let response = await axios(configs);

      if (!response || !response.data || response.data.byteLength <= 0) {
        ErrorToast(`Template not found.`);
        SetIsLoading(false);
        return;
      }

      var fileDataArray = [];
      fileDataArray.push(response.data);

      const url = window.URL.createObjectURL(
        new Blob(fileDataArray, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );

      const downloadFileName =
        modelType === "existing_PPA"
          ? benchMarkType
          : "Benchmark_Template_" + country;
      handleDownload(url, downloadFileName);
      SetIsLoading(false);
    } catch (error) {
      const errorMessage = getBufferErrorMessage(error);
      ErrorToast(errorMessage);
      SetIsLoading(false);
    }
  };

  const DownloadFile = async (
    name,
    userID,
    startDate = "",
    endDate = "",
    freequency = "",
    valuationAtInception = "",
    valuationClientType = "",
    yearEndDate = "",
    downloadFileName = "",
    isBESS = false,
  ) => {
    const params = {
      fileName: name,
      country: formData.location,
      userId: userID,
      startDate,
      endDate,
      valuationAtInception,
      freequency,
      yearEndDate,
      valuationClientType,
      type: "default",
    };

    if (formData?.modelType === "existing_PPA" || isBESS) {
      if (!startDate) {
        ErrorToast(`Please Provide Values For Model Start Date To Proceed.`);
        return;
      } else if (!endDate) {
        ErrorToast(`Please Provide Values For Contract End Date To Proceed.`);
        return;
      } else if (!freequency) {
        ErrorToast(
          `Please Provide Values For Valuation Freequency To Proceed.`,
        );
        return;
      } else if (freequency === "yearly" && !yearEndDate) {
        ErrorToast(`Please Provide Values For Year End Date To Proceed.`);
        return;
      }
    }

    var configs = {
      method: "post",
      url: `${config.BASE_URL}/api/ppa/downloadPPATemplate`,
      headers: {},
      data: params,
      responseType: "arraybuffer",
    };

    if (
      [
        "volumeCurve",
        "blackMarketPriceCurve",
        "greenMarketPriceCurve",
        "inflationCurve",
        "mlfCurve",
        "mlfCurveAtInception",
        "dlfCurve",
        "dlfCurveAtInception",
        "riskFreeRateCurve",
        "assetCreditRiskAdjustmentCurve",
        "liabilityCreditRiskAdjustmentCurve",
        "ppaEscalationCurve",
        "ppaPriceCurve",
        "greenMarketPriceCurveAtInception",
        "blackMarketPriceCurveAtInception",
        "volumeCurveAtInception",
        "curtailmentFactor",
        "curtailmentFactorAtInception",
        "unbundlingFactor",
        "inflationRateCurve",
        "discountRateCurve",
        "priceForecastCurve",
        "chargeForecastPriceCurve",
        "chargeMLFCurve",
        "disChargeMLFCurve",
      ].includes(name)
    ) {
      configs.url = `${config.BASE_URL}/api/ppa/download-valuation-template`;
    }
    try {
      let response = await axios(configs);

      if (!response || !response.data || response.data.byteLength <= 0) {
        ErrorToast(`Template not found.`);
        return;
      }

      var fileDataArray = [];
      fileDataArray.push(response.data);

      var url = window.URL.createObjectURL(
        new Blob(fileDataArray, {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      handleDownload(url, downloadFileName ? downloadFileName : name);
    } catch (error) {
      const errorMessage = getBufferErrorMessage(error);
      ErrorToast(errorMessage);
    }
  };

  const downloadValuationReport = async (payload) => {
    try {
      const configs = {
        method: "post",
        url: `${config.BASE_URL}/api/ppa/download-valuation-report`,
        headers: {},
        responseType: "arraybuffer",
        data: payload,
      };

      SetIsLoading(true);
      const response = await axios(configs);
      SetIsLoading(false);

      const file = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      // Build a URL from the file
      const fileURL = URL.createObjectURL(file);
      handleDownload(fileURL, "valuation-report", "docx");
    } catch (error) {
      SetIsLoading(false);
      ErrorToast("Failed to generate report. Please try again");
      console.log("Error *****", error);
    }
  };

  const handleDownload = (url, fileName, extension = "xlsx") => {
    const a = document.createElement("a");
    a.setAttribute("style", "display:none;");
    document.body.appendChild(a);
    a.href = url;
    a.target = "_blank";
    a.download = fileName + `.${extension}`;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const validateValues = (data) => {
    let errors = {};

    const re = /^\d+(\.\d{1,2})?$/;
    const customperentagere = /^0+(\.\d{1,2})?$/;
    const priceregex = /^\d+(\.\d{1,2})?$/;
    const pvregex = /^\d+(\.\d{1,2})?$/;
    const customnpvregex = /^-?\d+(\.\d{1,2})?$/;

    if (data?.modelType === "existing_PPA") {
      if (!data?.ppaLocation) {
        errors.ppaLocation = true;
      }

      if (!data?.valuationAtInception) {
        errors.valuationAtInception = true;
      }

      if (!data?.valuationClientType) {
        errors.valuationClientType = true;
      }

      if (data?.valuationClientType === "channel_1") {
        if (
          !data?.clientName ||
          !whiteSpaceStringRegex.test(data?.clientName)
        ) {
          errors.clientName = true;
        }

        if (
          !data?.projectName ||
          !whiteSpaceStringRegex.test(data?.projectName)
        ) {
          errors.projectName = true;
        }

        if (
          !data?.companyName ||
          !whiteSpaceStringRegex.test(data?.companyName)
        ) {
          errors.companyName = true;
        }

        if (
          !data?.entityName ||
          !whiteSpaceStringRegex.test(data?.entityName)
        ) {
          errors.entityName = true;
        }

        if (
          !data?.counterParty ||
          !whiteSpaceStringRegex.test(data?.counterParty)
        ) {
          errors.counterParty = true;
        }

        if (
          !data?.referenceDetails ||
          !whiteSpaceStringRegex.test(data?.referenceDetails)
        ) {
          errors.referenceDetails = true;
        }
      }

      if (!data?.valuationPerspective) {
        errors.valuationPerspective = true;
      }

      if (data?.valuationAtInception === "no") {
        if (
          !negativeNumberWithHigherLimitRegex.test(data?.calibrationAdjustment)
        ) {
          errors.calibrationAdjustment = true;
        }

        if (data?.calculateUnbundlingFactor === "yes") {
          if (
            data?.blackMarketPriceCurveAtInception === "" ||
            data?.blackMarketPriceCurveAtInception === undefined
          ) {
            errors.blackMarketPriceCurveAtInception = true;
          }

          if (
            data?.greenMarketPriceCurveAtInception === "" ||
            data?.greenMarketPriceCurveAtInception === undefined
          ) {
            errors.greenMarketPriceCurveAtInception = true;
          }

          if (
            data?.volumeCurveAtInception === "" ||
            data?.volumeCurveAtInception === undefined
          ) {
            errors.volumeCurveAtInception = true;
          }

          if (!data?.mlfIncludedInVolumeAtInception) {
            errors.mlfIncludedInVolumeAtInception = true;
          }

          if (
            data?.mlfIncludedInVolumeAtInception === "no" &&
            (data?.mlfCurveAtInception === "" ||
              data?.mlfCurveAtInception === undefined)
          ) {
            errors.mlfCurveAtInception = true;
          }

          if (!data?.dlfIncludedInVolumeAtInception) {
            errors.dlfIncludedInVolumeAtInception = true;
          }

          if (
            data?.dlfIncludedInVolumeAtInception === "no" &&
            (data?.dlfCurveAtInception === "" ||
              data?.dlfCurveAtInception === undefined)
          ) {
            errors.dlfCurveAtInception = true;
          }

          if (!data?.curtailmentIncludedInVolumeAtInception) {
            errors.curtailmentIncludedInVolumeAtInception = true;
          }

          if (
            data?.curtailmentIncludedInVolumeAtInception === "no" &&
            (data?.curtailmentFactorAtInception === "" ||
              data?.curtailmentFactorAtInception === undefined)
          ) {
            errors.curtailmentFactorAtInception = true;
          }
        }
      }

      if (!data?.basisOfCalibration) {
        errors.basisOfCalibration = true;
      }

      if (!data?.valuationDate) {
        errors.valuationDate = true;
      }

      if (!data?.modelStartDate) {
        errors.modelStartDate = true;
      }

      if (!data?.valuationFreequency) {
        errors.valuationFreequency = true;
      }

      if (!data?.contractStartDate) {
        errors.contractStartDate = true;
      }

      if (!data?.contractEndDate) {
        errors.contractEndDate = true;
      }

      if (!data?.useBenchmarkData) {
        errors.useBenchmarkData = true;
      }

      if (data?.useBenchmarkData === "yes" && !data?.forecastedDate) {
        errors.forecastedDate = true;
      }

      if (data?.valuationFreequency === "yearly" && !data?.yearEndDate) {
        errors.yearEndDate = true;
      }

      if (!data?.ppaVolStructure) {
        errors.ppaVolStructure = true;
      }

      if (!data?.changingVolumeInPPA) {
        errors.changingVolumeInPPA = true;
      }

      if (
        data?.changingVolumeInPPA === "no" &&
        !decimalNumberWithHigherLimitRegex.test(data?.annualPPAVolumeCapacity)
      ) {
        errors.annualPPAVolumeCapacity = true;
      }

      if (!data?.mlfIncludedInVolume) {
        errors.mlfIncludedInVolume = true;
      }

      if (!data?.dlfIncludedInVolume) {
        errors.dlfIncludedInVolume = true;
      }

      if (!data?.curtailmentIncludedInVolume) {
        errors.curtailmentIncludedInVolume = true;
      }

      if (data?.changingVolumeInPPA === "yes") {
        if (
          data?.volumeCurveFile === "" ||
          data?.volumeCurveFile === undefined
        ) {
          errors.volumeCurveFile = true;
        }

        if (!data?.generationType) {
          errors.generationType = true;
        }

        if (
          data?.generationType === "total_asset_generation" &&
          !decimalNumberWithHigherLimitRegex.test(data?.counterPartyAllocation)
        ) {
          errors.counterPartyAllocation = true;
        }
      }

      if (!data?.ppaPricing) {
        errors.ppaPricing = true;
      }

      if (
        data?.ppaPricing !== "custom" &&
        !decimalNumberWithHigherLimitRegex.test(data?.ppaPrice)
      ) {
        errors.ppaPrice = true;
      }

      if (
        data?.ppaPricing === "custom" &&
        (data?.ppaPriceCurve === "" || data?.ppaPriceCurve === undefined)
      ) {
        errors.ppaPriceCurve = true;
      }

      if (
        data?.ppaPricing !== "fix" &&
        (data?.ppaEscalationCurve === "" ||
          data?.ppaEscalationCurve === undefined)
      ) {
        errors.ppaEscalationCurve = true;
      }

      if (!data?.calculateUnbundlingFactor) {
        errors.calculateUnbundlingFactor = true;
      }

      if (!data?.unbundlingApproach) {
        errors.unbundlingApproach = true;
      }

      if (
        data?.calculateUnbundlingFactor === "no" &&
        ((data?.unbundlingApproach === "flat_unbundling" &&
          !decimalNumberWith2DecimalRegex.test(data?.unbundlingFactor)) ||
          (data?.unbundlingApproach !== "flat_unbundling" &&
            (data?.unbundlingFactor === "" ||
              data?.unbundlingFactor === undefined)))
      ) {
        errors.unbundlingFactor = true;
      }

      if (
        data?.calculateUnbundlingFactor === "yes" &&
        data?.unbundlingApproach === "flat_unbundling" &&
        !decimalNumberWith2DecimalRegex.test(data?.unbundlingRoundingMultiple)
      ) {
        errors.unbundlingRoundingMultiple = true;
      }

      if (
        data?.calculateUnbundlingFactor === "no" &&
        (data?.unbundlingFactor === "" || data?.unbundlingFactor === undefined)
      ) {
        errors.unbundlingFactor = true;
      }

      if (!data?.RETSchemeEndDate) {
        errors.RETSchemeEndDate = true;
      }

      if (!data?.discountingApproach) {
        errors.discountingApproach = true;
      }

      if (!data?.discountingConvention) {
        errors.discountingConvention = true;
      }

      if (Object.keys(errors).length === 0) {
        setDisableNext(false);
      } else {
        setDisableNext(true);
      }

      if (!data?.iscustomised) {
        errors.iscustomised = true;
      }

      if (data.iscustomised === "Yes") {
        if (data?.blackMarketPrice === "yes" && !data?.blackMarketPriceCurve) {
          errors.blackMarketPriceCurve = true;
        }

        if (
          data?.valuationAtInception === "yes" &&
          data?.calculateUnbundlingFactor === "yes"
        ) {
          if (
            data?.greenMarketPrice === "yes" &&
            !data?.greenMarketPriceCurve
          ) {
            errors.greenMarketPriceCurve = true;
          }
        }

        if (
          data?.mlfIncludedInVolume === "no" &&
          data?.uploadMLFCurve === "yes" &&
          !data?.mlfCurve
        ) {
          errors.mlfCurve = true;
        }

        if (
          data?.dlfIncludedInVolume === "no" &&
          data?.uploadDLFCurve === "yes" &&
          !data?.dlfCurve
        ) {
          errors.dlfCurve = true;
        }

        if (
          data?.curtailmentIncludedInVolume === "no" &&
          data?.uploadCurtailmentFactor === "yes" &&
          !data?.curtailmentFactor
        ) {
          errors.curtailmentFactor = true;
        }

        if (data?.inflation === "yes" && !data?.inflationCurve) {
          errors.inflationCurve = true;
        }

        if (data?.riskFreeRate === "yes" && !data?.riskFreeRateCurve) {
          errors.riskFreeRateCurve = true;
        }

        if (
          data?.assetCreditRiskAdjustment === "yes" &&
          !data?.assetCreditRiskAdjustmentCurve
        ) {
          errors.assetCreditRiskAdjustmentCurve = true;
        }

        if (
          data?.liabilityCreditRiskAdjustment === "yes" &&
          !data?.liabilityCreditRiskAdjustmentCurve
        ) {
          errors.liabilityCreditRiskAdjustmentCurve = true;
        }
      }
    } else {
      if (data?.location === "" || data?.location === undefined) {
        errors.location = true;
      }
      if (data?.conslocation === "" || data?.conslocation === undefined) {
        errors.conslocation = true;
      }
      if (
        data?.ppastartdate === "" ||
        data?.ppastartdate === undefined ||
        data?.ppastartdate === null
      ) {
        errors.ppastartdate = true;
      }
      if (data?.ppastartdate && data?.ppaenddate) {
        if (data?.ppaenddate < data?.ppastartdate) {
          errors.invaliddate = true;
        }
      }
      if (
        data?.ppaenddate === "" ||
        data?.ppaenddate === undefined ||
        data?.ppaenddate === null
      ) {
        errors.ppaenddate = true;
      }
      if (data?.technology === "Battery") {
        /**
         * TODO: Add Validation for Battery
         */
      } else {
        if (
          data?.ppavolstructure === "" ||
          data?.ppavolstructure === undefined
        ) {
          errors.ppavolstructure = true;
        }
        if (
          data?.ppavolstructure === "Pay as produced" &&
          (data?.benchmarkvolume === "" || data?.benchmarkvolume === undefined)
        ) {
          errors.benchmarkvolume = true;
        }
        if (
          data?.benchmarkvolume === "No" &&
          (data?.volume1CurveFile === "" ||
            data?.volume1CurveFile === undefined)
        ) {
          errors.volume1CurveFile = true;
        }
        if (
          data?.changevolume === "Yes" &&
          (data?.volume2CurveFile === "" ||
            data?.volume2CurveFile === undefined)
        ) {
          errors.volume2CurveFile = true;
        }
        if (
          data?.ppapricing === "Custom" &&
          (data?.price1CurveFile === "" || data?.price1CurveFile === undefined)
        ) {
          errors.price1CurveFile = true;
        }
        if (data?.ppapricing === "" || data?.ppapricing === undefined) {
          errors.ppapricing = true;
        }
        if (data?.discountfactor === "" || data?.discountfactor === undefined) {
          errors.discountfactor = true;
        }
        if (
          data?.degradation === "Custom fixed" &&
          (data?.cuspercentage === undefined ||
            data?.cuspercentage === undefined ||
            !customperentagere.test(data?.cuspercentage))
        ) {
          errors.cuspercentage = true;
        }
        if (
          data?.discountfactor === "Custom fixed" &&
          (data?.customnpv === undefined ||
            data?.customnpv === "" ||
            !customnpvregex.test(data?.customnpv))
        ) {
          errors.customnpv = true;
        }
        if (
          (data?.ppapricing === "Fix" ||
            data?.ppapricing === "Indexed with CPI") &&
          (data?.ppaprice === undefined ||
            data?.ppaprice === "" ||
            !priceregex.test(data?.ppaprice))
        ) {
          errors.ppaprice = true;
        }
        if (
          (data?.ppavolstructure === "Baseload" ||
            data?.benchmarkvolume === "Yes") &&
          data?.changevolume === "No" &&
          (data?.annualvolcapacity === undefined ||
            data?.annualvolcapacity === "" ||
            !re.test(data?.annualvolcapacity))
        ) {
          errors.annualvolcapacity = true;
        }
        if (
          (data?.ppavolstructure === "Baseload" ||
            data?.benchmarkvolume === "Yes") &&
          data?.changevolume === "No" &&
          (data?.annualVolumeUnit === undefined ||
            data?.annualVolumeUnit === "")
        ) {
          errors.annualVolumeUnit = true;
        }
        if (
          data?.ppavolstructure === "Pay as produced" &&
          data?.benchmarkvolume === "Yes" &&
          data?.changevolume === "No" &&
          (data?.technology === "" || data?.technology === undefined)
        ) {
          errors.technology = true;
        }

        if (
          data?.technology === "Mix PV (Fixed) & Battery" &&
          data?.benchmarkvolume === "Yes" &&
          data?.changevolume === "No"
        ) {
          if (
            data?.pvshare === "" ||
            data?.pvshare === undefined ||
            !pvregex.test(data?.pvshare)
          ) {
            errors.pvshare = true;
          }

          if (data?.degradation === "" || data?.degradation === undefined) {
            errors.degradation = true;
          }

          if (!pvregex.test(data?.roundTripEfficiency)) {
            errors.roundTripEfficiency = true;
          }

          if (!pvregex.test(data?.batteryDegradation)) {
            errors.batteryDegradation = true;
          }
        }

        if (
          data?.technology === "Mix PV (fixed) & Wind (onshore)" &&
          data?.benchmarkvolume === "Yes" &&
          data?.changevolume === "No" &&
          (data?.pvshare === "" ||
            data?.pvshare === undefined ||
            !pvregex.test(data?.pvshare))
        ) {
          errors.pvshare = true;
        }
        if (
          (data?.ppavolstructure === "Baseload" ||
            data?.benchmarkvolume === "Yes") &&
          (data?.changevolume === "" || data?.changevolume === undefined)
        ) {
          errors.changevolume = true;
        }
        if (
          (data?.technology === "PV (fixed)" ||
            data?.technology === "Mix PV (fixed) & Wind (onshore)") &&
          data?.benchmarkvolume === "Yes" &&
          data?.changevolume === "No" &&
          (data?.degradation === "" || data?.degradation === undefined)
        ) {
          errors.degradation = true;
        }
      }

      if (Object.keys(errors).length === 0) {
        setDisableNext(false);
      } else {
        setDisableNext(true);
      }

      // PPA Parameter Validation

      if (!data?.iscustomised || data?.iscustomised === "") {
        errors.iscustomised = true;
      }

      if (data?.iscustomised === "Yes") {
        if (!data?.powerPriceCurve || data?.powerPriceCurve === "") {
          errors.powerPriceCurve = true;
        }

        if (!data?.gooPriceCurve || data?.gooPriceCurve === "") {
          errors.gooPriceCurve = true;
        }

        if (
          !data?.carbonIntensityOfGrid ||
          data?.carbonIntensityOfGrid === ""
        ) {
          errors.carbonIntensityOfGrid = true;
        }

        if (
          data?.powerPriceCurve === "Yes" &&
          (!data?.powerPriceCurveFile || data?.powerPriceCurveFile == "")
        ) {
          errors.powerPriceCurveFile = true;
        }

        if (
          data?.gooPriceCurve === "Yes" &&
          (!data?.gooPriceCurveFile || data?.gooPriceCurveFile == "")
        ) {
          errors.gooPriceCurveFile = true;
        }

        if (
          data?.carbonIntensityOfGrid === "Yes" &&
          (!data?.carbonIntensityOfGridFile ||
            data?.carbonIntensityOfGridFile == "")
        ) {
          errors.carbonIntensityOfGridFile = true;
        }
      }
    }

    if (Object.keys(errors).length === 0) {
      setDisableCalculate(false);
    } else {
      setDisableCalculate(true);
    }

    SetErrors(errors);
  };

  const ValidateTemplate = async (
    file,
    templateName,
    fromBenchmark = false,
    payload = {},
  ) => {
    try {
      if (
        formData?.modelType === "new_PPA" ||
        (fromBenchmark === true && payload.modelType === "new_PPA")
      ) {
        if (
          templateName != templateNames.benchmarkFile &&
          (!formData.ppastartdate || !formData.ppaenddate)
        ) {
          //ErrorToast(`Please Select Start And End Date To Procced.`);
          return (result = "Not Valid");
        } else {
          let form = new FormData();

          form.append("templateName", templateName);
          form.append("template", file, templateName);

          if (
            [
              "volumeCurve",
              "inflationRateCurve",
              "discountRateCurve",
              "priceForecastCurve",
              "chargeForecastPriceCurve",
              "chargeMLFCurve",
              "disChargeMLFCurve",
            ].includes(templateName)
          ) {
            const data = {};

            data.startDate = formData?.ppastartdate
              ? moment(formData?.ppastartdate)
                  .startOf("month")
                  .format("YYYY-MM-DD")
              : null;
            data.endDate = formData?.ppaenddate
              ? moment(formData?.ppaenddate).endOf("month").format("YYYY-MM-DD")
              : null;
            data.valuationFreequency = formData?.valuationFreequency;
            data.valuationAtInception = "no";
            data.type = "default";
            data.valuationClientType = "channel_2";

            if (
              formData?.valuationFreequency === "yearly" &&
              formData?.yearEndDate
            ) {
              data.yearEndDate = moment(formData?.yearEndDate)
                .endOf("month")
                .format("YYYY-MM-DD");
            }

            if (
              !data.startDate ||
              !data?.endDate ||
              !data?.valuationFreequency ||
              !data?.valuationAtInception ||
              (data?.valuationFreequency === "yearly" && !data?.yearEndDate) ||
              !data?.valuationClientType
            ) {
              return (result = "bad_request");
            }

            form.append("startDate", data?.startDate);
            form.append("endDate", data?.endDate);
            form.append("valuationFreequency", data?.valuationFreequency);
            form.append("valuationAtInception", data?.valuationAtInception);
            form.append("type", data.type);

            if (data.valuationFreequency === "yearly") {
              form.append("yearEndDate", data?.yearEndDate);
            }

            form.append("valuationClientType", data?.valuationClientType);

            SetIsLoading(true);
            const result = await axios
              .post(
                `${config.BASE_URL}/api/ppa/validate-valuation-template`,
                form,
                {
                  headers: {
                    "Content-Type": "multipart/form-data",
                  },
                },
              )
              .then((response) => {
                SetIsLoading(false);
                return response.data;
              });

            return result;
          } else {
            form.append(
              "ppastartdate",
              formData?.ppastartdate
                ? moment(formData?.ppastartdate).format("YYYY-MM")
                : null,
            );
            form.append(
              "ppaenddate",
              formData?.ppaenddate
                ? moment(formData?.ppaenddate).format("YYYY-MM")
                : null,
            );
            form.append(
              "ppavolstructure",
              formData?.ppavolstructure ? formData?.ppavolstructure : null,
            );

            SetIsLoading(true);
            var result = await axios
              .post(`${config.BASE_URL}/api/ppa/validateTemplate`, form, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              })
              .then((response) => {
                SetIsLoading(false);
                return response.data;
              });

            return result;
          }
        }
      } else if (
        formData?.modelType === "existing_PPA" ||
        (fromBenchmark === true && payload.modelType === "existing_PPA")
      ) {
        const data = {};

        let startDate;

        if (fromBenchmark) {
          startDate = payload?.startDate;
        } else {
          if (
            [
              "volumeCurveAtInception",
              "blackMarketPriceCurveAtInception",
              "greenMarketPriceCurveAtInception",
              "mlfCurveAtInception",
              "dlfCurveAtInception",
              "curtailmentFactorAtInception",
            ].includes(templateName)
          ) {
            startDate = formData?.contractStartDate;
            if (formData?.valuationFreequency === "yearly") {
              let yearEndDate;

              const modelStartDate = moment(
                formData?.modelStartDate,
                "YYYY-MM-DD",
              );
              const contractStartDate = moment(
                formData?.contractStartDate,
                "YYYY-MM-DD",
              );

              if (!modelStartDate.isSame(contractStartDate)) {
                const yearEnd = moment(formData?.yearEndDate, "YYYY-MM-DD");
                if (modelStartDate.year() === yearEnd.year()) {
                  yearEndDate = yearEnd.format(
                    `${contractStartDate.year()}-MM-DD`,
                  );
                } else {
                  yearEndDate = yearEnd.format(
                    `${contractStartDate.year() + 1}-MM-DD`,
                  );
                }
              } else {
                yearEndDate = formData?.yearEndDate;
              }

              data.yearEndDate = yearEndDate;
            }
          } else {
            startDate = formData?.modelStartDate;
            if (formData?.valuationFreequency === "yearly") {
              data.yearEndDate = formData?.yearEndDate;
            }
          }
        }

        data.modelStartDate = startDate;
        data.contractEndDate = fromBenchmark
          ? payload?.endDate
          : formData?.contractEndDate;
        data.valuationFreequency = fromBenchmark
          ? "monthly"
          : formData?.valuationFreequency;
        data.valuationAtInception = fromBenchmark
          ? "no"
          : formData?.valuationAtInception;

        if (!fromBenchmark) {
          data.valuationClientType = formData?.valuationClientType;
        }

        if (
          !data.modelStartDate ||
          !data?.contractEndDate ||
          !data?.valuationFreequency ||
          !data?.valuationAtInception ||
          (!fromBenchmark &&
            data.valuationFreequency === "yearly" &&
            !data?.yearEndDate) ||
          (!fromBenchmark && !data?.valuationClientType)
        ) {
          return (result = "bad_request");
        } else {
          let form = new FormData();

          form.append("templateName", templateName);
          form.append("startDate", data?.modelStartDate);
          form.append("endDate", data?.contractEndDate);
          form.append("valuationFreequency", data?.valuationFreequency);
          form.append("valuationAtInception", data?.valuationAtInception);
          form.append("template", file, templateName);
          form.append("type", fromBenchmark ? "benchmark" : "default");

          if (!fromBenchmark) {
            if (data.valuationFreequency === "yearly") {
              form.append("yearEndDate", data?.yearEndDate);
            }

            form.append("valuationClientType", data?.valuationClientType);
          }

          SetIsLoading(true);
          const result = await axios
            .post(
              `${config.BASE_URL}/api/ppa/validate-valuation-template`,
              form,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              },
            )
            .then((response) => {
              SetIsLoading(false);
              return response.data;
            });

          return result;
        }
      }
    } catch (err) {
      SetIsLoading(false);
      const errorMessage = getErrorMessage(err);
      ErrorToast(errorMessage);
    }
  };

  const saveValuationDetails = async () => {
    try {
      const {
        benchmarkBlackMarketPriceCurve,
        benchmarkCurtailmentFactor,
        benchmarkDLFCurve,
        benchmarkMLFCurve,
        benchmarkLiabilityCreditRiskAdjustmentCurve,
        benchmarkAssetCreditRiskAdjustmentCurve,
        benchmarkGreenMarketPriceCurve,
        benchmarkInflationCurve,
        benchmarkRiskFreeRateCurve,
        useCustomAssetCreditRiskAdjustmentCurve,
        useCustomBlackMarketPriceCurve,
        useCustomCurtailmentFactor,
        useCustomDLFCurve,
        useCustomGreenMarketPriceCurve,
        useCustomInflationCurve,
        useCustomLiabilityCreditRiskAdjustmentCurve,
        useCustomMLFCurve,
        useCustomRiskFreeRateCurve,
        blackMarketPriceCurve,
        blackMarketPriceCurveAtInception,
        greenMarketPriceCurve,
        greenMarketPriceCurveAtInception,
        inflationCurve,
        riskFreeRateCurve,
        assetCreditRiskAdjustmentCurve,
        liabilityCreditRiskAdjustmentCurve,
        mlfCurve,
        dlfCurve,
        curtailmentFactor,
        ...otherResultData
      } = resultData;

      const data = {
        ppaLocation,
        valuationClientType: formData?.valuationClientType,
        valuationAtInception: formData?.valuationAtInception,
        valuationPerspective: formData?.valuationPerspective,
        basisOfCalibration: formData?.basisOfCalibration,
        valuationDate: formData?.valuationDate,
        modelStartDate,
        valuationFreequency: formData?.valuationFreequency,
        contractStartDate: formData?.contractStartDate,
        contractEndDate: formData?.contractEndDate,
        forecastedDate: formData?.forecastedDate,
        counterPartyAllocation: formData?.counterPartyAllocation,
        ppaVolStructure: formData?.ppaVolStructure,
        generationType: formData?.generationType,
        changingVolumeInPPA: formData?.changingVolumeInPPA,
        mlfIncludedInVolume: formData?.mlfIncludedInVolume,
        dlfIncludedInVolume: formData?.dlfIncludedInVolume,
        curtailmentIncludedInVolume: formData?.curtailmentIncludedInVolume,
        ppaPricing: formData?.ppaPricing,
        calculateUnbundlingFactor: formData?.calculateUnbundlingFactor,
        unbundlingApproach: formData?.unbundlingApproach,
        RETSchemeEndDate: formData?.RETSchemeEndDate,
        discountingApproach: formData?.discountingApproach,
        discountingConvention: formData?.discountingConvention,
        iscustomised: formData?.iscustomised,
        useBenchmarkData,
      };

      if (formData?.iscustomised === "Yes") {
        if (useCustomAssetCreditRiskAdjustmentCurve) {
          data.assetCreditRiskAdjustmentCurve = assetCreditRiskAdjustmentCurve;
        }

        if (useCustomLiabilityCreditRiskAdjustmentCurve) {
          data.liabilityCreditRiskAdjustmentCurve =
            liabilityCreditRiskAdjustmentCurve;
        }

        if (useCustomRiskFreeRateCurve) {
          data.riskFreeRateCurve = riskFreeRateCurve;
        }

        if (useCustomInflationCurve) {
          data.inflationCurve = inflationCurve;
        }

        if (useCustomMLFCurve) {
          data.mlfCurve = mlfCurve;
        }

        if (useCustomDLFCurve) {
          data.dlfCurve = dlfCurve;
        }

        if (useCustomCurtailmentFactor) {
          data.curtailmentFactor = curtailmentFactor;
        }

        if (useCustomBlackMarketPriceCurve) {
          data.blackMarketPriceCurve = blackMarketPriceCurve;
        }

        if (useCustomGreenMarketPriceCurve) {
          data.greenMarketPriceCurve = greenMarketPriceCurve;
        }
      }

      if (formData?.valuationFreequency === "yearly") {
        data.yearEndDate = formData?.yearEndDate;
      }

      if (formData?.changingVolumeInPPA === "no") {
        data.annualPPAVolumeCapacity = formData?.annualPPAVolumeCapacity;
      }

      if (formData?.ppaPricing !== "custom") {
        data.ppaPrice = formData?.ppaPrice;
      }

      if (
        formData?.calculateUnbundlingFactor === "yes" &&
        formData?.unbundlingApproach === "flat_unbundling"
      ) {
        data.unbundlingRoundingMultiple = formData?.unbundlingRoundingMultiple;
      }

      if (
        formData?.valuationAtInception === "no" &&
        formData?.calculateUnbundlingFactor === "yes"
      ) {
        data.mlfIncludedInVolumeAtInception =
          formData?.mlfIncludedInVolumeAtInception;
        data.dlfIncludedInVolumeAtInception =
          formData?.dlfIncludedInVolumeAtInception;
        data.curtailmentIncludedInVolumeAtInception =
          formData?.curtailmentIncludedInVolumeAtInception;
        data.blackMarketPriceCurveAtInception =
          blackMarketPriceCurveAtInception;
        data.greenMarketPriceCurveAtInception =
          greenMarketPriceCurveAtInception;
      }

      if (formData?.valuationClientType === "channel_1") {
        data.clientName = formData?.clientName;
        data.projectName = formData?.projectName;
        data.companyName = formData?.companyName;
        data.entityName = formData?.entityName;
        data.counterParty = formData?.counterParty;
        data.referenceDetails = formData?.referenceDetails;
      }

      const payload = {
        valuationDate: formData?.valuationDate,
        data: JSON.stringify({
          ...otherResultData,
          ...data,
          useCustomAssetCreditRiskAdjustmentCurve,
          useCustomBlackMarketPriceCurve,
          useCustomCurtailmentFactor,
          useCustomDLFCurve,
          useCustomGreenMarketPriceCurve,
          useCustomInflationCurve,
          useCustomLiabilityCreditRiskAdjustmentCurve,
          useCustomMLFCurve,
          useCustomRiskFreeRateCurve,
          iscustomised: formData?.iscustomised
            ? formData?.iscustomised?.toLowerCase()
            : "no",
        }),
      };

      const configs = {
        method: "post",
        url: `${config.BASE_URL}/api/ppa/save-existing-ppa-valuation`,
        headers: {},
        data: payload,
      };

      SetIsLoading(true);
      const response = await axios(configs);
      SetIsLoading(false);
      return response.data;
    } catch (error) {
      SetIsLoading(false);
      console.log("Error ***", error);
      return { status: "error" };
    }
  };

  const getValuationDetails = async (id, mode) => {
    try {
      const configs = {
        method: "get",
        url: `${config.BASE_URL}/api/ppa/valuation/${id}`,
        headers: {},
      };

      SetIsLoading(true);
      const response = await axios(configs);
      SetIsLoading(false);
      if (response.data) {
        const valuationInputs = response.data?.valuationInputs || {};
        const valuationResultData = response.data?.valuationResultData || {};
        if (mode === "read") {
          if (valuationInputs?.changingVolumeInPPA === "yes") {
            valuationInputs.volumeCurveFile = createExcelFile("volumeCurve");
          }

          valuationInputs.readOnly = true;

          valuationInputs.calibrationAdjustment = Number(
            valuationInputs?.calibrationAdjustment?.toFixed(8) || 0,
          );

          if (valuationInputs?.ppaPricing !== "fix") {
            valuationInputs.ppaEscalationCurve =
              createExcelFile("ppaEscalationCurve");
          }

          if (valuationInputs?.ppaPricing === "custom") {
            valuationInputs.ppaPriceCurve = createExcelFile("ppaPriceCurve");
          }

          if (
            valuationInputs?.valuationAtInception === "no" &&
            valuationInputs?.calculateUnbundlingFactor === "yes"
          ) {
            valuationInputs.greenMarketPriceCurveAtInception = createExcelFile(
              "greenMarketPriceCurveAtInception",
            );
            valuationInputs.blackMarketPriceCurveAtInception = createExcelFile(
              "blackMarketPriceCurveAtInception",
            );
            valuationInputs.volumeCurveAtInception = createExcelFile(
              "volumeCurveAtInception",
            );

            if (valuationInputs?.mlfIncludedInVolumeAtInception === "no") {
              valuationInputs.mlfCurveAtInception = createExcelFile(
                "mlfCurveAtInception",
              );
            }

            if (valuationInputs?.dlfIncludedInVolumeAtInception === "no") {
              valuationInputs.dlfCurveAtInception = createExcelFile(
                "dlfCurveAtInception",
              );
            }

            if (
              valuationInputs?.curtailmentIncludedInVolumeAtInception === "no"
            ) {
              valuationInputs.curtailmentFactorAtInception = createExcelFile(
                "curtailmentFactorAtInception",
              );
            }
          }

          if (
            valuationInputs?.calculateUnbundlingFactor === "no" &&
            valuationInputs?.unbundlingApproach
          ) {
            if (valuationInputs?.unbundlingApproach === "flat_unbundling") {
              valuationInputs.unbundlingFactor = valuationResultData
                ?.unbundlingFactor?.[0]?.factor
                ? valuationResultData.unbundlingFactor[0].factor * 100
                : "";
            } else {
              valuationInputs.unbundlingFactor =
                createExcelFile("unbundlingFactor");
            }
          }

          if (valuationInputs?.iscustomised === "yes") {
            valuationInputs.iscustomised = "Yes";

            if (valuationResultData?.useCustomBlackMarketPriceCurve) {
              valuationInputs.blackMarketPrice = "yes";
              valuationInputs.blackMarketPriceCurve = createExcelFile(
                "blackMarketPriceCurve",
              );
            }

            if (valuationResultData?.useCustomGreenMarketPriceCurve) {
              valuationInputs.greenMarketPrice = "yes";
              valuationInputs.greenMarketPriceCurve = createExcelFile(
                "greenMarketPriceCurve",
              );
            }

            if (
              valuationResultData?.useCustomMLFCurve &&
              valuationInputs?.mlfIncludedInVolume === "no"
            ) {
              valuationInputs.uploadMLFCurve = "yes";
              valuationInputs.mlfCurve = createExcelFile("mlfCurve");
            }

            if (
              valuationResultData?.useCustomDLFCurve &&
              valuationInputs?.dlfIncludedInVolume === "no"
            ) {
              valuationInputs.uploadDLFCurve = "yes";
              valuationInputs.dlfCurve = createExcelFile("dlfCurve");
            }

            if (
              valuationResultData?.useCustomCurtailmentFactor &&
              valuationInputs?.curtailmentIncludedInVolume === "no"
            ) {
              valuationInputs.uploadCurtailmentFactor = "yes";
              valuationInputs.curtailmentFactor =
                createExcelFile("curtailmentFactor");
            }

            if (valuationResultData?.useCustomInflationCurve) {
              valuationInputs.inflation = "yes";
              valuationInputs.inflationCurve =
                createExcelFile("inflationCurve");
            }

            if (valuationResultData?.useCustomRiskFreeRateCurve) {
              valuationInputs.riskFreeRate = "yes";
              valuationInputs.riskFreeRateCurve =
                createExcelFile("riskFreeRateCurve");
            }

            if (valuationResultData?.useCustomAssetCreditRiskAdjustmentCurve) {
              valuationInputs.assetCreditRiskAdjustment = "yes";
              valuationInputs.assetCreditRiskAdjustmentCurve = createExcelFile(
                "assetCreditRiskAdjustmentCurve",
              );
            }

            if (
              valuationResultData?.useCustomLiabilityCreditRiskAdjustmentCurve
            ) {
              valuationInputs.liabilityCreditRiskAdjustment = "yes";
              valuationInputs.liabilityCreditRiskAdjustmentCurve =
                createExcelFile("liabilityCreditRiskAdjustmentCurve");
            }
          } else {
            valuationInputs.iscustomised = "No";
          }

          SetFormData({ modelType: "existing_PPA", ...valuationInputs });
          SetResultData(valuationResultData);
        } else if (mode === "new") {
          const {
            ppaLocation,
            valuationClientType,
            valuationPerspective,
            basisOfCalibration,
            calibrationAdjustment,
            valuationFreequency,
            contractStartDate,
            contractEndDate,
            counterPartyAllocation,
            ppaVolStructure,
            ppaPrice,
            unbundlingApproach,
            unbundlingRoundingMultiple,
            clientName,
            companyName,
            counterParty,
            projectName,
            entityName,
            referenceDetails,
          } = valuationInputs;
          const inputs = {
            ppaLocation,
            valuationClientType,
            valuationPerspective,
            basisOfCalibration,
            calibrationAdjustment: Number(calibrationAdjustment?.toFixed(8)),
            valuationFreequency,
            contractStartDate,
            contractEndDate,
            counterPartyAllocation,
            ppaVolStructure,
            ppaPrice,
            unbundlingApproach,
            unbundlingRoundingMultiple,
          };

          if (valuationClientType === "channel_1") {
            inputs.clientName = clientName;
            inputs.companyName = companyName;
            inputs.counterParty = counterParty;
            inputs.projectName = projectName;
            inputs.entityName = entityName;
            inputs.referenceDetails = referenceDetails;
          }

          inputs.modelType = "existing_PPA";
          inputs.reValuation = true;
          inputs.valuationAtInception = "no";
          inputs.calculateUnbundlingFactor = "no";
          inputs.calculatedUnbundlingFactor =
            valuationResultData?.unbundlingFactor;

          if (unbundlingApproach === "flat_unbundling") {
            inputs.unbundlingFactor = valuationResultData?.unbundlingFactor?.[0]
              ?.factor
              ? valuationResultData.unbundlingFactor[0].factor * 100
              : "";
          }

          SetFormData({ ...inputs });
        }
        SetActiveTabId("2");
      }
      return response.data;
    } catch (error) {
      SetIsLoading(false);
      console.log("Error ***", error);
      return { status: "error" };
    }
  };

  return (
    <PPAContext.Provider
      value={{
        tabs: tabs,
        isLoading: isLoading,
        SetIsLoading,
        formData: formData,
        SetFormData,
        disableNext,
        disableCalculate,
        errors: errors,
        SetErrors,
        activeTabId: activeTabId,
        SetActiveTabId,
        resultData,
        SetResultData,
        DownloadFile,
        downloadValuationReport,
        DownloadBenchmarkTemplate,
        templateNames,
        ValidateTemplate,
        forecastedDates,
        setForecastedDates,
        saveValuationDetails,
        getValuationDetails,
        scenarioResults,
        setScenarioResults,
      }}
    >
      {children}
    </PPAContext.Provider>
  );
};

export default PPAContext;
